import { memo } from 'react';
import type { OrderWithItems } from '@/store/seller';
import type { OrderType, OrderStatus } from '@/types';
import { PickupTimeDisplay } from '@/components/order/PickupTimeDisplay';

interface OrderDetailModalProps {
  isOpen: boolean;
  order: OrderWithItems | null;
  onClose: () => void;
}

const orderTypeLabels: Record<OrderType, string> = {
  DEADLINE_DISCOUNT: '마감할인',
  RESERVATION_DISCOUNT: '예약할인',
  REGULAR_ORDER: '일반주문'
};

const orderTypeBadgeColors: Record<OrderType, string> = {
  DEADLINE_DISCOUNT: 'bg-orange-100 text-orange-600',
  RESERVATION_DISCOUNT: 'bg-blue-100 text-blue-600',
  REGULAR_ORDER: 'bg-gray-100 text-gray-600'
};

const orderStatusLabels: Record<OrderStatus, string> = {
  PENDING: '접수대기',
  ACCEPTED: '준비중',
  READY: '픽업대기',
  COMPLETED: '픽업완료',
  CANCELLED: '취소됨',
  NOSHOW: '노쇼'
};

const orderStatusColors: Record<OrderStatus, string> = {
  PENDING: 'text-yellow-600',
  ACCEPTED: 'text-blue-600',
  READY: 'text-green-600',
  COMPLETED: 'text-gray-600',
  CANCELLED: 'text-red-600',
  NOSHOW: 'text-orange-600'
};

export const OrderDetailModal = memo(function OrderDetailModal({
  isOpen,
  order,
  onClose
}: OrderDetailModalProps) {
  if (!isOpen || !order) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getTotalQuantity = () => {
    return order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold">주문 상세</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 영수증 스타일 내용 */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            {/* 주문번호 & 상태 */}
            <div className="text-center pb-3 border-b border-gray-200">
              <p className="text-2xl font-bold mb-2">{order.order_number}</p>
              <p className="text-sm text-gray-600 mb-2">{formatDateTime(order.created_at)}</p>
              <div className="flex justify-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${orderTypeBadgeColors[order.order_type]}`}>
                  {orderTypeLabels[order.order_type]}
                </span>
                <span className={`font-medium text-sm ${orderStatusColors[order.status]}`}>
                  {orderStatusLabels[order.status]}
                </span>
              </div>
            </div>

            {/* 주문자 정보 */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm">주문자</span>
                <span className="font-medium">{order.buyer_nickname}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm">수령시간</span>
                <PickupTimeDisplay 
                  order={{
                    ...order,
                    items: order.items?.map(item => ({
                      ...item,
                      product: item.products ? { id: '', name: item.products.name, original_price: 0 } : undefined
                    }))
                  } as any}
                  className="text-blue-600 text-sm justify-end"
                />
              </div>
              {order.payment_status && (
                <div className="flex justify-between">
                  <span className="text-gray-600 text-sm">결제상태</span>
                  <span className={`font-medium ${
                    order.payment_status === 'PAID' ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    {order.payment_status === 'PAID' ? '결제완료' : '결제대기'}
                  </span>
                </div>
              )}
            </div>

            {/* 주문 상품 목록 */}
            <div className="border-t border-gray-200 pt-3">
              <h3 className="font-medium mb-2">주문 상품</h3>
              <div className="space-y-2">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <div className="flex-1">
                      <span className="font-medium">{item.product_name}</span>
                      <span className="text-gray-600 ml-2">x{item.quantity}</span>
                    </div>
                    <span className="font-medium">
                      {formatCurrency((item.unit_price || item.price || 0) * item.quantity)}원
                    </span>
                  </div>
                )) || (
                  <p className="text-gray-500 text-sm">상품 정보가 없습니다</p>
                )}
              </div>
            </div>

            {/* 금액 정보 */}
            <div className="border-t border-gray-200 pt-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm">상품 수량</span>
                <span className="font-medium">{getTotalQuantity()}개</span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span>총 결제금액</span>
                <span className="text-green-600">{formatCurrency(order.total_amount)}원</span>
              </div>
            </div>

            {/* 취소 사유 (취소된 주문인 경우) */}
            {order.status === 'CANCELLED' && order.cancellation_reason && (
              <div className="border-t border-gray-200 pt-3">
                <h3 className="font-medium mb-1 text-red-600">취소 사유</h3>
                <p className="text-sm text-gray-700">{order.cancellation_reason}</p>
              </div>
            )}
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="p-4 border-t">
          <button
            onClick={onClose}
            className="w-full py-3 px-4 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
});